import { useEffect, useMemo, useState } from 'react';
import { useBahasa } from '../i18n';

/**
 * Paging untuk daftar panjang.
 *
 * Satu komponen dipakai bersama Kamera, User, dan Dashboard List supaya
 * kalimat posisinya sama persis di semua halaman — termasuk dengan bilah
 * halaman di Dashboard, yang sudah lebih dulu memakai bentuk kalimat ini.
 *
 * Daftar kamera tumbuh terus (96 dan bertambah), dan halaman yang menurunkan
 * seluruhnya sekaligus memaksa orang menggulung jauh hanya untuk sampai ke
 * tombol di bawah.
 */
export function usePaging<T>(items: T[], perPage: number) {
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / perPage));
  // Halaman yang sedang dibuka bisa lenyap saat filter menyempit atau sebuah
  // baris dihapus; jangan biarkan orang terdampar di halaman kosong.
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * perPage;

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const slice = useMemo(
    () => items.slice(start, start + perPage),
    [items, start, perPage],
  );

  return { page: safePage, setPage, pageCount, start, slice, total: items.length };
}

export function Pager({
  page,
  pageCount,
  start,
  shown,
  total,
  noun,
  onPage,
}: {
  page: number;
  pageCount: number;
  start: number;
  shown: number;
  total: number;
  /** Jamak, huruf kecil — mis. `cameras`. */
  noun: string;
  onPage: (p: number) => void;
}) {
  const { t } = useBahasa();
  if (total === 0) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm text-muted" aria-live="polite">
        {pageCount === 1
          ? t('{total} {noun}', { total, noun: t(noun) })
          : t('Page {page} of {pageCount} — {noun} {dari}–{sampai} of {total}', {
              page: page + 1,
              pageCount,
              noun: t(noun),
              dari: start + 1,
              sampai: start + shown,
              total,
            })}
      </span>
      <NomorHalaman page={page} pageCount={pageCount} onPage={onPage} />
    </div>
  );
}

/**
 * Berapa nomor halaman yang tampak sekaligus (§G8, 24 Agustus 2026).
 *
 * Empat, diminta lapangan. Yang digantikan adalah sepasang tombol
 * `Previous`/`Next` berlabel kata plus kalimat posisi di sebelahnya — di bilah
 * dashboard rangkaian itu selalu memaksa bilahnya membungkus jadi dua baris,
 * dan baris kedua memakan tinggi grid.
 */
const JENDELA = 4;

/**
 * Halaman yang sedang dibuka selalu jadi nomor **paling kiri**, kecuali di
 * ujung daftar tempat jendelanya mentok.
 *
 * Bentuk yang diminta: di halaman 1 tertulis `1 2 3 4`, dan di halaman 4
 * tertulis `4 5 6 7`. Jendela yang memusatkan halaman aktif menghasilkan
 * `3 4 5 6` untuk kasus kedua, jadi bukan itu yang dipakai.
 */
function jendela(page: number, pageCount: number): number[] {
  const mulai = Math.max(0, Math.min(page, pageCount - JENDELA));
  return Array.from({ length: Math.min(JENDELA, pageCount) }, (_, i) => mulai + i);
}

/** Ikon tunggal/ganda. Chevron, bukan huruf `<` — huruf ikut ukuran font. */
const CHEVRON = {
  awal: 'M17 6l-5 6 5 6M11 6l-5 6 5 6',
  mundur: 'M14.5 6l-5 6 5 6',
  maju: 'M9.5 6l5 6-5 6',
  akhir: 'M7 6l5 6-5 6M13 6l5 6-5 6',
} as const;

/**
 * Ukuran tombol: lebar minimal 32px, tinggi 44px.
 *
 * Tinggi penuh `min-h-tap` (§G7); lebarnya yang dipangkas, dengan alasan yang
 * sama seperti tombol ikon kartu kamera (§G11) — delapan tombol selebar 44px
 * berarti 352px yang habis hanya untuk paging, dan di bilah dashboard itu
 * mengembalikan baris kedua yang justru sedang dibuang. Terukur di bilah
 * dashboard pada 1280px: isinya 1.091px sebelum dipangkas, 1.032px yang
 * tersedia. 32×44 tetap jauh di atas ambang 24×24 WCAG 2.5.8.
 *
 * `min-w`, bukan `w`: nomor tiga angka (halaman 100 ke atas) melebarkan
 * tombolnya sendiri lewat `px-1.5` alih-alih terpotong.
 */
const TOMBOL =
  'flex min-h-tap min-w-[2rem] items-center justify-center rounded-md px-1.5 text-sm tabular-nums transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40 motion-reduce:transition-none';

function TombolArah({
  d,
  label,
  disabled,
  onClick,
}: {
  d: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`${TOMBOL} border border-line`}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={d} />
      </svg>
    </button>
  );
}

/**
 * Paging bernomor: `« ‹ 1 2 3 4 › »` (§G8, 24 Agustus 2026).
 *
 * Satu komponen untuk seluruh halaman yang punya paging — Cameras, Users,
 * Camera Groups, Logs, dan bilah dashboard — supaya loncat halaman bekerja
 * dengan cara yang sama di semuanya.
 *
 * `«`/`»` hanya muncul kalau halamannya lebih banyak daripada yang muat di
 * jendela; di bawah itu ia mengerjakan hal yang persis sama dengan `‹`/`›`.
 */
export function NomorHalaman({
  page,
  pageCount,
  onPage,
  className = '',
}: {
  /** Berbasis nol. */
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
  className?: string;
}) {
  const { t } = useBahasa();
  if (pageCount <= 1) return null;

  const nomor = jendela(page, pageCount);
  const adaUjung = pageCount > JENDELA;

  return (
    <div
      className={`flex items-center gap-0.5 ${className}`}
      role="group"
      aria-label={t('Pagination')}
    >
      {adaUjung && (
        <TombolArah
          d={CHEVRON.awal}
          label={t('First page')}
          disabled={page === 0}
          onClick={() => onPage(0)}
        />
      )}
      <TombolArah
        d={CHEVRON.mundur}
        label={t('Previous page')}
        disabled={page === 0}
        onClick={() => onPage(Math.max(0, page - 1))}
      />

      {nomor.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onPage(n)}
          // `aria-current` yang menyatakan "ini halaman yang sedang dibuka";
          // warnanya sendiri tidak dibacakan siapa pun.
          aria-current={n === page ? 'page' : undefined}
          aria-label={t('Page {page}', { page: n + 1 })}
          className={`${TOMBOL} ${
            n === page
              ? 'border border-accent bg-subtle font-semibold text-accent-text'
              : 'border border-line'
          }`}
        >
          {n + 1}
        </button>
      ))}

      <TombolArah
        d={CHEVRON.maju}
        label={t('Next page')}
        disabled={page >= pageCount - 1}
        onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
      />
      {adaUjung && (
        <TombolArah
          d={CHEVRON.akhir}
          label={t('Last page')}
          disabled={page >= pageCount - 1}
          onClick={() => onPage(pageCount - 1)}
        />
      )}
    </div>
  );
}
